import { createChipSuggestion } from "../rich-chip-editor/chip-suggestion";
import { EntityRefList, type EntityRefListProps } from "./entity-ref-list";
import { ENTITY_TAGGABLE_TYPES } from "@quovy/types/chip-doc/entity-refs";
import type { EntityTaggable } from "./types";

/** Server `/agents/taggables` cap per family; keep in lockstep with `PER_TYPE` there. */
const PER_TYPE = 6;
/** 6 per type × every {@link ENTITY_TAGGABLE_TYPES} section, so none is starved. */
const MAX_NON_FIELD_RESULTS = PER_TYPE * ENTITY_TAGGABLE_TYPES.length;

type SearchFn = (query: string) => Promise<EntityTaggable[]>;

/**
 * Builds the `@`-suggestion config for the AOP editor on top of the shared
 * chip-suggestion machinery. `getSearch` returns the latest async source and
 * `getAddIntegrationHref` the latest "Add integration" URL (both passed
 * through refs so the editor isn't rebuilt when they change).
 */
export function createEntitySuggestion(
  getSearch: () => SearchFn | undefined,
  getAddIntegrationHref?: () => string | undefined,
  getOnCreateStagedFolder?: () => EntityRefListProps["onCreateStagedFolder"],
) {
  return createChipSuggestion<EntityTaggable, EntityRefListProps>({
    char: "@",
    // Entity labels are prose names ("Call Details", "Q3 Submission.pdf"), and
    // a field query carries the model name verbatim — without this the session
    // dies at the first space and `@Call Details.` is left in the document as
    // literal text.
    allowSpaces: true,
    async items(query) {
      const search = getSearch();
      if (!search) return [];
      // Cap at 6 per type × every section so later groups (skills,
      // integrations) still appear when earlier ones are full; the popup
      // scrolls past what fits. A FIELD query (`Model.partial`) is exempt:
      // its rows are one model's fields rather than types competing for
      // popup space, and the server already caps them — slicing here would
      // silently drop fields from a wide model.
      const results = await search(query);
      if (results.some((item) => item.field)) return results;
      return results.slice(0, MAX_NON_FIELD_RESULTS);
    },
    componentProps: (props) => {
      const addIntegrationHref = getAddIntegrationHref?.();
      const onCreateStagedFolder = getOnCreateStagedFolder?.();
      return {
        items: props.items,
        command: props.command,
        query: props.query,
        atQueryEnd: props.editor.state.selection.from === props.range.to,
        onQueryChange: (next: string) => {
          const from = props.range.from + 1;
          const to = props.range.to;
          if (from > to) return;
          props.editor.view.dispatch(props.editor.state.tr.insertText(next, from, to));
        },
        onFocusEditor: () => {
          props.editor.commands.focus();
        },
        ...(addIntegrationHref !== undefined ? { addIntegrationHref } : {}),
        ...(onCreateStagedFolder !== undefined ? { onCreateStagedFolder } : {}),
        onAddIntegration: () => {
          const href = getAddIntegrationHref?.();
          props.editor.chain().focus().deleteRange(props.range).run();
          if (href) window.open(href, "_blank", "noopener,noreferrer");
        },
        onDrillCustomModel: (modelName: string) => {
          // Text node, not a string: Tiptap parses strings as HTML (`Q1 <2024>`).
          props.editor
            .chain()
            .focus()
            .insertContentAt(props.range, [{ type: "text", text: `@${modelName}.` }])
            .run();
        },
      };
    },
    component: EntityRefList,
  });
}
